import React, { FormEvent, useState } from 'react';
import { config } from '../../config';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export const Contact = () => {
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    if (formData.get('_gotcha')) {
      return;
    }

    try {
      setSubmitState('submitting');
      setErrorMessage('');

      const response = await fetch(config.formspree_endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Unable to send message right now. Please try again later.');
      }

      setSubmitState('success');
      form.reset();
    } catch (error) {
      setSubmitState('error');
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <section id="contact" className="section-padding bg-accent">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-primary font-bold tracking-[0.3em] uppercase text-xs mb-6">Connect</p>
            <h2 className="text-4xl sm:text-6xl font-bold mb-8 leading-tight">
              Get in <span className="text-primary italic font-light">Touch</span>
            </h2>
            <p className="text-lg text-secondary/60 mb-12 max-w-md">
              Have questions or want to learn more about our church? We'd love to hear from you.
            </p>

            <div className="space-y-10">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm border border-secondary/5">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-secondary mb-1">Visit Us</h4>
                  {config.address.map((line, i) => (
                    <p key={i} className="text-secondary/60 text-sm">{line}</p>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm border border-secondary/5">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-secondary mb-1">Email Us</h4>
                  <a href={`mailto:${config.email_address}`} className="text-primary font-bold hover:underline">{config.email_address}</a>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm border border-secondary/5">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-secondary mb-1">Follow Us</h4>
                  <div className="flex gap-4 mt-2">
                    <a href={config.socials.facebook} target="_blank" rel="noopener noreferrer" className="text-secondary/40 hover:text-primary transition-colors">Facebook</a>
                    <a href={config.socials.youtube} target="_blank" rel="noopener noreferrer" className="text-secondary/40 hover:text-primary transition-colors">YouTube</a>
                    <a href={config.socials.tiktok} target="_blank" rel="noopener noreferrer" className="text-secondary/40 hover:text-primary transition-colors">TikTok</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-10 sm:p-16 rounded-[3rem] shadow-2xl border border-secondary/5 w-full">
            <h3 className="text-3xl font-bold mb-10">Send a Message</h3>

            {submitState === 'success' ? (
              <div className="rounded-2xl bg-green-50 border border-green-200 p-6">
                <p className="font-semibold text-green-700">Thank you. Your message has been sent.</p>
                <button
                  type="button"
                  onClick={() => setSubmitState('idle')}
                  className="mt-4 text-primary font-semibold hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form className="space-y-8" onSubmit={handleSubmit} noValidate>
                <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" />

                <div className="space-y-2">
                  <label htmlFor="contact-name" className="text-xs font-bold uppercase tracking-widest text-secondary/40">Full Name</label>
                  <input id="contact-name" name="name" type="text" required className="w-full bg-accent px-6 py-4 rounded-2xl border border-secondary/5 focus:border-primary outline-none transition-all" placeholder="Your name" />
                </div>

                <div className="space-y-2">
                  <label htmlFor="contact-email" className="text-xs font-bold uppercase tracking-widest text-secondary/40">Email Address</label>
                  <input id="contact-email" name="email" type="email" required className="w-full bg-accent px-6 py-4 rounded-2xl border border-secondary/5 focus:border-primary outline-none transition-all" placeholder="your@email.com" />
                </div>

                <div className="space-y-2">
                  <label htmlFor="contact-message" className="text-xs font-bold uppercase tracking-widest text-secondary/40">Message</label>
                  <textarea id="contact-message" name="message" required rows={5} className="w-full bg-accent px-6 py-4 rounded-2xl border border-secondary/5 focus:border-primary outline-none transition-all resize-none" placeholder="How can we help you?" />
                </div>

                {submitState === 'error' && (
                  <p role="alert" className="text-sm text-red-600">{errorMessage}</p>
                )}

                <button type="submit" disabled={submitState === 'submitting'} className="btn-primary w-full disabled:opacity-70">
                  {submitState === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
